import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Dispatch status: "Sent today" before the UK weekday cutoff, otherwise the next dispatch date.
 * Optionally shows a countdown to the cutoff and an estimated arrival window counted in
 * UK business days after dispatch.
 * @extends {Component<{estimate: HTMLElement, dispatchDate?: HTMLElement, countdown?: HTMLElement, arrival?: HTMLElement}>}
 */
class EvelynDelivery extends Component {
  requiredRefs = ['estimate'];
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#refresh();
    clearInterval(this.#timer);
    this.#timer = setInterval(() => this.#refresh(), 60000);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#timer);
  }

  #refresh() {
    const now = new Date();
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(now).map(part => [part.type, part.value]));
    const day = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12));
    const holidays = (this.dataset.holidays || '').split(',').map(value => value.trim());
    const businessDay = date => ![0, 6].includes(date.getUTCDay()) && !holidays.includes(date.toISOString().slice(0, 10));
    const addBusinessDays = (from, count) => {
      const date = new Date(from);
      for (let added = 0; added < count;) {
        date.setUTCDate(date.getUTCDate() + 1);
        if (businessDay(date)) added++;
      }
      return date;
    };
    const format = date => new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', {
      timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short',
    }).format(date);
    const formatDispatch = date => {
      if (this.dataset.dateFormat !== 'numeric') return format(date);
      const fields = Object.fromEntries(new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', {
        timeZone: 'UTC', weekday: 'short', day: '2-digit', month: '2-digit',
      }).formatToParts(date).map(part => [part.type, part.value]));
      return `${fields.weekday} ${fields.day}/${fields.month}`;
    };

    const cutoff = Number(this.dataset.cutoff ?? 14);
    const minutes = cutoff * 60 - Number(parts.hour) * 60 - Number(parts.minute);
    const beforeCutoff = businessDay(day) && minutes > 0;
    const { countdown, arrival } = this.refs;

    if (!beforeCutoff) {
      do { day.setUTCDate(day.getUTCDate() + 1); } while (!businessDay(day));
    }

    if (this.refs.dispatchDate) {
      this.refs.dispatchDate.textContent = formatDispatch(day);
    } else if (beforeCutoff && this.dataset.dateFormat !== 'numeric') {
      this.refs.estimate.textContent = this.dataset.today || '';
    } else {
      this.refs.estimate.textContent = (this.dataset.later || '').replace('__DATE__', formatDispatch(day));
    }

    if (countdown) {
      const time = `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
      countdown.textContent = beforeCutoff ? (this.dataset.countdown || '').replace('__TIME__', time) : '';
      countdown.hidden = !beforeCutoff;
    }

    const min = Number(this.dataset.daysMin);
    const max = Number(this.dataset.daysMax);
    if (arrival && min > 0) {
      const start = addBusinessDays(day, min);
      const end = addBusinessDays(day, Math.max(min, max || min));
      const range = start.getTime() === end.getTime() ? format(start) : `${format(start)} – ${format(end)}`;
      arrival.textContent = (this.dataset.arrival || '').replace('__RANGE__', range);
      arrival.hidden = false;
    }
  }
}

if (!customElements.get('evelyn-delivery')) customElements.define('evelyn-delivery', EvelynDelivery);

/** Refreshes variant-dependent regions (gallery art, stock note) after the variant response settles. */
class EvelynBenefits extends Component {
  #controller;
  #version = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#update, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#version++;
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #update = async event => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const version = ++this.#version;
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || version !== this.#version || String(detail?.productId) !== this.dataset.productId) return;
      const next = detail.html?.querySelector(`evelyn-benefits[data-live="${this.dataset.live}"]`);
      if (next) this.replaceChildren(...Array.from(next.childNodes));
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[evelyn-benefits] Variant update failed:', error);
    }
  };
}

if (!customElements.get('evelyn-benefits')) customElements.define('evelyn-benefits', EvelynBenefits);

/**
 * Featured-image gallery: keeps the thumbnail row and "n / total" counter in sync with the
 * swipeable track, and scrolls the track when a thumbnail is chosen.
 * @extends {Component<{track: HTMLElement, slides: HTMLElement[], thumbs?: HTMLButtonElement[], counter?: HTMLElement}>}
 */
class EvelynGallery extends Component {
  requiredRefs = ['track'];
  #controller;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }

  /** @param {MouseEvent} event */
  select(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-index]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;
    const slide = this.refs.slides?.[Number(button.dataset.index)];
    if (!slide) return;
    const { track } = this.refs;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    this.#mark(Number(button.dataset.index));
  }

  #listen() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.refs.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#sync());
    }, { passive: true, signal: this.#controller.signal });
  }

  #sync() {
    const { track } = this.refs;
    if (!track.clientWidth) return;
    const index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
    this.#mark(index);
  }

  /** @param {number} index */
  #mark(index) {
    const total = this.refs.slides?.length ?? 1;
    const current = Math.min(Math.max(index, 0), total - 1);
    for (const [position, thumb] of (this.refs.thumbs ?? []).entries()) {
      const active = position === current;
      thumb.setAttribute('aria-current', String(active));
      const row = thumb.parentElement;
      if (active && row) {
        // Scroll only the thumbnail row horizontally so the page never jumps vertically.
        const delta = thumb.getBoundingClientRect().left - row.getBoundingClientRect().left;
        row.scrollBy({ left: delta - (row.clientWidth - thumb.clientWidth) / 2 });
      }
    }
    if (this.refs.counter) this.refs.counter.textContent = `${current + 1} / ${total}`;
  }
}

if (!customElements.get('evelyn-gallery')) customElements.define('evelyn-gallery', EvelynGallery);
